<template>
  <div class="gateway-block">
    <category-title :title="$t('gateways.title')" withActions>
      <app-switch-toggle
        v-model="showInactive"
        @change="onToggleShowInactive"
        plate
        id="switch_gateways_show_inactive"
        :label="$t('gateways.show_inactive', 'Show inactive')"
      />
      <gateway-add ref="gatewayModal" @create-entity="createEntity" />
    </category-title>

    <b-alert :show="$route.query.i === '1'" variant="info">
      <div>
        {{
          $t(
            "gateways.create_instruction",
            'To create gateway click "Add new gateway" and select some gateway'
          )
        }}
      </div>
      <div v-if="hasGateways">
        {{ $t("gateways.create_instruction_has_disabled", "Or activate some from exists") }}
      </div>
    </b-alert>

    <div v-if="!loading && hasAccess">
      <div
        v-for="[id, data] in Object.entries(visibleGateways.groups)"
        :key="id"
        class="border p-2 mb-2 mx-n2 mt-5"
      >
        <div class="group-section mb-2 shift-up">
          <div class="bg-white border p-2 d-flex align-items-center flex-wrap group-section__row">
            <div class="d-flex align-items-center group-section__identity">
              <img
                v-if="data.logo"
                :src="data.logo"
                height="25px"
                class="mx-2 group-section__logo"
                :alt="data.gtw"
              />
              <div class="d-flex flex-column">
                <span class="text-uppercase">{{ data.entityName }}</span>
                <small v-if="data.accountNumber" class="text-grey">{{ data.accountNumber }}</small>
              </div>

              <b-badge
                v-if="data.multiOrgStripeAccount"
                pill
                variant="warning"
                class="group-section__multi-org-badge"
                :title="
                  $t(
                    'gateways.multi_org_stripe_account_tooltip',
                    'This Stripe account is also used by other organizations, so balance and payout data aren\'t shown here.'
                  )
                "
              >
                {{ $t("gateways.multi_org_stripe_account", "Shared with other organizations") }}
              </b-badge>
            </div>

            <div v-if="data.balanceStatus && crowdedBaseGateway(data)" class="group-section__stats">
              <b-skeleton v-if="data.balanceStatus === 'pending'" width="220px" height="14px" />
              <span v-else-if="data.balanceStatus === 'error'" class="text-grey">
                {{ $t("gateways.balance_unavailable", "Unavailable") }}
              </span>
              <crowded-balance-stages
                v-else-if="data.balanceStatus === 'ready'"
                :balance="data.balance"
                :payout-account="crowdedPayoutAccount(data)"
              />
            </div>

            <div v-else-if="data.balanceStatus" class="group-section__stats">
              <div class="group-section__stat">
                <b-skeleton v-if="data.balanceStatus === 'pending'" width="120px" height="14px" />
                <template v-else-if="data.balanceStatus === 'ready' && availableBalance(data)">
                  <span class="group-section__stat-label">
                    {{ $t("gateways.balance_available", "Available") }}
                  </span>
                  <span class="group-section__stat-value">{{ availableBalance(data) }}</span>
                </template>
                <span v-else-if="data.balanceStatus === 'error'" class="text-grey">
                  {{ $t("gateways.balance_unavailable", "Unavailable") }}
                </span>
              </div>

              <div
                class="group-section__stat"
                v-if="data.balanceStatus === 'ready' && pendingBalance(data)"
              >
                <span class="group-section__stat-label">
                  {{ $t("gateways.balance_pending", "Pending") }}
                </span>
                <span class="group-section__stat-value">{{ pendingBalance(data) }}</span>
              </div>

              <div
                class="group-section__stat"
                v-if="data.balanceStatus === 'ready' && capturedBalance(data)"
              >
                <span class="group-section__stat-label">
                  {{ $t("gateways.balance_captured", "Captured") }}
                </span>
                <span class="group-section__stat-value">{{ capturedBalance(data) }}</span>
              </div>

              <div
                class="group-section__stat"
                v-if="data.balanceStatus === 'ready' && inTransitBalance(data)"
              >
                <span class="group-section__stat-label">
                  {{ $t("gateways.balance_in_transit", "In transit") }}
                </span>
                <span class="group-section__stat-value">{{ inTransitBalance(data) }}</span>
              </div>

              <div class="group-section__stat" v-if="data.payoutSchedule">
                <span class="group-section__stat-label">
                  {{ $t("gateways.payout_schedule", "Payout schedule") }}
                </span>
                <span class="group-section__stat-value">
                  {{ payoutScheduleText(data.payoutSchedule) }}
                </span>
              </div>
            </div>

            <div v-if="crowdedBaseGateway(data)" class="group-section__stats">
              <div v-if="crowdedPayoutState(data)" class="group-section__stat">
                <span class="group-section__stat-label">
                  {{ $t("gateways.crowded_payouts_label", "Payouts") }}
                </span>
                <span
                  class="group-section__stat-value"
                  :class="
                    crowdedPayoutState(data) === 'automatic' ? 'text-success' : 'text-warning'
                  "
                >
                  {{
                    crowdedPayoutState(data) === "automatic"
                      ? $t("gateways.crowded_payouts_automatic", "Automatic")
                      : $t("gateways.crowded_payouts_paused", "Paused")
                  }}
                </span>
              </div>
              <button
                type="button"
                class="bttn bttn--sm bttn--blue"
                @click="openModals(crowdedBaseGateway(data))"
              >
                {{ $t("gateways.crowded_manage_payouts", "Manage payouts") }}
              </button>
            </div>
          </div>
          <div
            v-if="
              data.list.filter((item) => item.attributes.gateway === 'stripe-element').length > 0
            "
          >
            <button
              class="bttn bttn--sm bttn--orange"
              @click="openMorePaymentMethodsModal(data.list)"
            >
              {{ $t("gateways.more_payment_methods", "More payment methods") }}
            </button>
          </div>
        </div>
        <gatewayPlate
          v-for="item in data.list"
          :key="item.id"
          :gatewaydata="item"
          @view-data="openModals"
        />
      </div>

      <gatewayPlate
        v-for="item in visibleGateways.rest"
        :key="item.id"
        :gatewaydata="item"
        @view-data="openModals"
      />
    </div>

    <div class="loading" v-if="loading && hasAccess">
      <app-spinner />
    </div>

    <div v-if="!hasGateways && !loading && hasAccess" class="empty">
      {{ $t("gateways.not_have_gateways") }}
    </div>

    <div v-if="!hasAccess" class="empty">{{ $t("gateways.not_have_gateways_access") }}</div>

    <div class="entities-modal">
      <entities-modal ref="entitiesModal" :gateway-modal-ref="$refs.gatewayModal" />
    </div>

    <more-payment-methods-modal ref="morePaymentMethodsModal" />

    <!-- Stripe Connect Status Modal -->
    <b-modal
      v-model="showConnectStatusModal"
      :title="
        connectStatus === 'success'
          ? $t('gateways.connect_success_title', 'Success')
          : $t('gateways.connect_error_title', 'Error')
      "
      :ok-only="true"
      :ok-title="$t('gateways.close', 'Close')"
      @ok="closeConnectStatusModal"
      @hidden="closeConnectStatusModal"
    >
      <div :class="connectStatus === 'success' ? 'text-success' : 'text-danger'">
        {{ connectMessage }}
      </div>
    </b-modal>
  </div>
</template>

<script>
import categoryTitle from "@/common-components/ui-elements/category-title.vue";
import gatewayPlate from "./subcomponents/gatewayPlate.vue";
import gatewayAdd from "./subcomponents/gatewayAdd.vue";
import entitiesModal from "@/views/account/subcomponents/entitiesModal.vue";
import appSpinner from "@/common-components/ui-elements/app-spinner.vue";
import AppSwitchToggle from "@/common-components/ui-elements/app-switch-toggle.vue";
import store from "../../store/store.js";
import { mapState } from "vuex";
import MorePaymentMethodsModal from "./subcomponents/morePaymentMethodsModal.vue";
import { formatBalanceAmounts } from "@/helpers/format-money.js";
import CrowdedBalanceStages from "./subcomponents/crowdedBalanceStages.vue";
import { formatPayoutSchedule } from "@/helpers/format-payout-schedule.js";

const SHOW_INACTIVE_STORAGE_KEY = "gateways_show_inactive";

export default {
  data() {
    return {
      showConnectStatusModal: false,
      connectStatus: null,
      connectMessage: "",
      balancePollInterval: null,
      // Enabled by default; only an explicit "0" in storage turns it off.
      showInactive: localStorage.getItem(SHOW_INACTIVE_STORAGE_KEY) !== "0",
    };
  },

  created() {
    store.dispatch("getGateways").then(() => {
      this.maybePollBalances();
      // Best-effort, per Crowded chapter: without it the header just shows
      // "Manage payouts".
      store.state.gateways.gateways
        .filter((gtw) => gtw.attributes.gateway === "crowded")
        .forEach((gtw) => {
          store.dispatch("fetchCrowdedPayoutAccount", gtw.id).catch(() => {});
        });
    });
    store.dispatch("getEntities");
  },

  beforeDestroy() {
    if (this.balancePollInterval) {
      clearInterval(this.balancePollInterval);
    }
  },

  mounted() {
    // Check for Stripe Connect redirect parameters
    const { status, message } = this.$route.query;
    if (status && message) {
      this.connectStatus = status;
      this.connectMessage = decodeURIComponent(message);
      this.showConnectStatusModal = true;
    }
  },

  components: {
    categoryTitle,
    gatewayPlate,
    gatewayAdd,
    entitiesModal,
    appSpinner,
    AppSwitchToggle,
    MorePaymentMethodsModal,
    CrowdedBalanceStages,
  },

  computed: {
    ...mapState({
      hasGateways(state) {
        return state.gateways.gateways.length;
      },

      gateways: (state) => {
        const stripeLogo =
          "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/charidy_images/gateways/stripe.png";
        const crowdedLogo =
          "https://content.pstmn.io/aa044a46-6390-49c1-a025-0b1a53b78cb7/Y3Jvd2RlZC1sb2dvLnBuZw==";
        // Crowded card + its inline siblings share one Crowded account, so group them together.
        const crowdedFamily = ["crowded", "crowded-digital-wallet", "crowded-ach"];

        const buildGroup = (res, gtw, gID, meta) => {
          const attrs = gtw.attributes;
          const data = res.groups[gID] || {};
          const list = data.list || [];
          list.push(gtw);
          res.groups[gID] = {
            list,
            gtw: attrs.gateway,
            entityName: attrs.entity_name,
            accountNumber: attrs.gateway_data.account_number,
            // Only one member of the group (e.g. the base "stripe"/"crowded"
            // gateway, not its siblings) carries balance data — surface it
            // once on the shared group header rather than per-plate.
            balanceStatus: data.balanceStatus || attrs.balance_status || null,
            balance: data.balance || attrs.balance || null,
            payoutSchedule: data.payoutSchedule || attrs.payout_schedule || null,
            multiOrgStripeAccount: data.multiOrgStripeAccount || !!attrs.multi_org_stripe_account,
            ...meta,
          };
          return res;
        };

        return state.gateways.gateways.reduce(
          (res, gtw) => {
            const attrs = gtw.attributes;
            const name = attrs.gateway;

            if (name.includes("stripe") && attrs.gateway_data.account_number) {
              const gID = "stripe-" + attrs.gateway_data.account_number;
              res = buildGroup(res, gtw, gID, { logo: stripeLogo });
            } else if (crowdedFamily.includes(name)) {
              const gID = "crowded-" + (attrs.entity_id || "default");
              res = buildGroup(res, gtw, gID, { logo: crowdedLogo });
            } else {
              res.rest.push(gtw);
            }

            return res;
          },
          {
            groups: {},
            rest: [],
          }
        );
      },

      loading: (state) => {
        return state.gateways.loadingGateways;
      },

      hasAccess: (state) => {
        return state.account.access.CanSeeOrganizationGatewayList;
      },

      crowdedPayoutAccounts: (state) => state.gateways.crowdedPayoutAccounts,

      hasPendingBalances: (state) => {
        return state.gateways.gateways.some((gtw) => gtw.attributes.balance_status === "pending");
      },
    }),

    // this.gateways above is the full grouped/ungrouped set; filter out
    // inactive gateways (and any group left empty by that) when the toggle
    // is off. Plain computed (not part of mapState) so it can read
    // this.showInactive alongside store state.
    visibleGateways() {
      if (this.showInactive) {
        return this.gateways;
      }

      const groups = {};
      for (const [id, data] of Object.entries(this.gateways.groups)) {
        const list = data.list.filter((item) => item.attributes.active);
        if (list.length) {
          groups[id] = { ...data, list };
        }
      }

      return {
        groups,
        rest: this.gateways.rest.filter((item) => item.attributes.active),
      };
    },
  },

  methods: {
    // v-model already updated this.showInactive by the time the native
    // "change" event fires (it listens on "input"), so just persist the
    // current value rather than trust the raw event arg.
    onToggleShowInactive() {
      localStorage.setItem(SHOW_INACTIVE_STORAGE_KEY, this.showInactive ? "1" : "0");
    },

    // Balance/payout-schedule data is fetched asynchronously on the backend
    // (cached for 1h); while any gateway reports balance_status "pending",
    // poll silently until it resolves to "ready"/"error".
    maybePollBalances() {
      if (this.hasPendingBalances && !this.balancePollInterval) {
        this.balancePollInterval = setInterval(() => {
          store.dispatch("refreshGateways").then(() => {
            if (!this.hasPendingBalances) {
              clearInterval(this.balancePollInterval);
              this.balancePollInterval = null;
            }
          });
        }, 8000);
      }
    },

    availableBalance(groupData) {
      return formatBalanceAmounts(groupData.balance?.available, { skipZero: true });
    },

    pendingBalance(groupData) {
      return formatBalanceAmounts(groupData.balance?.pending, { skipZero: true });
    },

    capturedBalance(groupData) {
      return formatBalanceAmounts(groupData.balance?.captured, { skipZero: true });
    },

    inTransitBalance(groupData) {
      return formatBalanceAmounts(groupData.balance?.in_transit, { skipZero: true });
    },

    payoutScheduleText(schedule) {
      return formatPayoutSchedule(schedule, this.$t.bind(this));
    },

    // The group's base "crowded" gateway — the one whose details modal holds
    // the payout settings — or null for non-Crowded groups.
    crowdedBaseGateway(groupData) {
      return groupData.list.find((item) => item.attributes.gateway === "crowded") || null;
    },

    // The group's Crowded payout account, or null when not loaded.
    crowdedPayoutAccount(groupData) {
      const base = this.crowdedBaseGateway(groupData);
      return (base && this.crowdedPayoutAccounts[base.id]) || null;
    },

    // "automatic" | "paused" once the group's payout account is set up,
    // otherwise null (not loaded yet, or no account — the header then only
    // offers "Manage payouts").
    crowdedPayoutState(groupData) {
      const account = this.crowdedPayoutAccount(groupData);
      if (!account || account.status === "missing") return null;
      return account.enabled ? "automatic" : "paused";
    },

    openModals(getawaysIngo) {
      this.$refs.gatewayModal.openModalView(getawaysIngo);
    },

    openMorePaymentMethodsModal(list) {
      this.$refs.morePaymentMethodsModal.open(
        list.filter((item) => item.attributes.gateway_data.payment_method_types)
      );
    },

    createEntity: function (gatewayStep) {
      this.$refs.entitiesModal.openModal(gatewayStep);
    },

    closeConnectStatusModal() {
      this.showConnectStatusModal = false;

      // Clean up URL parameters
      const query = { ...this.$route.query };
      delete query.status;
      delete query.message;

      this.$router.replace({ query });
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .gateway-plate {
  &:not(:last-of-type) {
    margin-bottom: 10px;
  }
}

// Don't touch below styles, it hide button from Entity modal
.gateway-block {
  ::v-deep .entities-modal > div {
    > button {
      display: none;
    }
  }
}

.group-section {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 12px;

  &__row {
    flex: 0 1 auto;
    row-gap: 10px;
  }

  &__logo {
    max-width: 80px;
    object-fit: contain;
  }

  &__multi-org-badge {
    margin-left: 16px;
    font-size: 12px;
    font-weight: 600;
    padding: 5px 12px;
    cursor: default;
  }

  &__stats {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 16px;
    margin-left: 30px;
    padding: 6px 16px;
    background-color: #f5f6f8;
    border-radius: 6px;
  }

  &__stat {
    display: flex;
    flex-direction: column;
    font-size: 14px;
  }

  &__stat-label {
    color: #bbbcc4;
  }

  &__stat-value {
    font-weight: 700;
    color: $fd;
  }
}

.text-grey {
  color: #bbbcc4;
}

.shift-up {
  margin-top: -2.5rem;
}
</style>
